/**
 * Workflow Configuration Component Registry
 *
 * Central registry for tag-based workflow configuration components
 * Maps workflow tags (normalized to lowercase) to specific configuration components
 */

import { WorkflowConfigComponent } from '../../types/workflow-config';
import { DefaultConfig } from './DefaultConfig';
import { ColdMailConfig } from './ColdMailConfig';
import { InsertConfig } from './InsertConfig';

/**
 * Registry mapping tag names to configuration components
 * Tag names are normalized to lowercase for matching
 *
 * IMPORTANT: When adding new tags here, also add descriptions to TAG_DESCRIPTIONS below
 */
const registry = new Map<string, WorkflowConfigComponent>([
  ['coldmail', ColdMailConfig],
  ['insert', InsertConfig],
  // Add more workflow configurations here as they are created
  // Example: ['coldmail-premium', ColdMailPremiumConfig],
  // Example: ['survey', SurveyConfig],
]);

/**
 * Tag descriptions for UI display
 * IMPORTANT: Keep this in sync with the registry above
 */
const TAG_DESCRIPTIONS: Record<string, string> = {
  'coldmail': 'Cold mail campaign configuration',
  'insert': 'CSV upload for data insertion workflows',
  // Add descriptions for new tags here
};

/**
 * Tag priority order (highest to lowest)
 * More specific tags should be listed first
 * This ensures that if a workflow has multiple matching tags,
 * the most specific configuration is used
 */
const TAG_PRIORITIES: string[] = [
  'coldmail-premium', // More specific variants first
  'coldmail',
  'insert',
  // Add more tags in priority order
];

/**
 * Get the appropriate configuration component for a workflow tag
 * @param tagName - The workflow tag name (case-insensitive)
 * @returns The configuration component for the tag, or DefaultConfig if not found
 */
export function getWorkflowConfigComponent(tagName: string): WorkflowConfigComponent {
  const normalizedTag = tagName.toLowerCase().trim();
  return registry.get(normalizedTag) || DefaultConfig;
}

/**
 * Get configuration component from workflow tags array
 * Uses tag priority order to select the most appropriate component
 * @param tags - Array of workflow tags
 * @returns The configuration component, or DefaultConfig if no matching tag found
 */
export function getConfigComponentFromTags(
  tags?: Array<{ id: number; name: string }>
): WorkflowConfigComponent {
  if (!tags || tags.length === 0) {
    return DefaultConfig;
  }

  // Normalize all tag names for matching
  const tagNames = tags.map((t) => t.name.toLowerCase().trim());

  // First, check priority list for most specific match
  for (const priorityTag of TAG_PRIORITIES) {
    if (tagNames.includes(priorityTag)) {
      const component = registry.get(priorityTag);
      if (component) {
        return component;
      }
    }
  }

  // Fallback: Try to find any matching tag in registry (original behavior)
  for (const tag of tags) {
    const normalizedTag = tag.name.toLowerCase().trim();
    const component = registry.get(normalizedTag);
    if (component) {
      return component;
    }
  }

  // No matching tag found, return default
  return DefaultConfig;
}

/**
 * Available workflow configuration tag
 */
export interface WorkflowConfigTag {
  value: string;
  label: string;
  description: string;
}

/**
 * Get all available workflow configuration tags
 * Automatically extracts from registry for dynamic extensibility
 * @returns Array of available tags with metadata
 */
export function getAvailableWorkflowTags(): WorkflowConfigTag[] {
  const tags: WorkflowConfigTag[] = [];

  // Extract all tag names from registry
  for (const [tagName] of registry) {
    tags.push({
      value: tagName,
      label: tagName.charAt(0).toUpperCase() + tagName.slice(1), // Capitalize first letter
      description: TAG_DESCRIPTIONS[tagName] || 'Custom workflow configuration',
    });
  }

  return tags.sort((a, b) => a.label.localeCompare(b.label));
}
